import {
  ItemPriceTier,
  liveSubscriptionStatuses,
  pluralize,
  SubscriptionItemType,
} from "core";
import { useAuthContext } from "../auth/useAuthContext";
import { useIsGranted } from "../auth/useIsGranted";
import { Maybe } from "./misc";
import { trpc } from "./trpc";

function getSubtotal(tiers: Maybe<ItemPriceTier[]>, quantity: number): number {
  if (!tiers?.length || quantity <= 0) return 0;

  const result = tiers.reduce(
    (acc, tier) => {
      if (acc.remainingQuantity <= 0) {
        return acc;
      }

      const tierLimit = tier.upTo ?? Infinity; // No limit if upTo is null
      const applicableQuantity = Math.min(
        acc.remainingQuantity,
        tierLimit - acc.processedQuantity,
      );

      if (tier.flatAmountDecimal) {
        acc.totalCost += parseFloat(tier.flatAmountDecimal) / 100;
      }
      if (tier.unitAmountDecimal) {
        acc.totalCost +=
          applicableQuantity * (parseFloat(tier.unitAmountDecimal) / 100);
      }

      acc.remainingQuantity -= applicableQuantity;
      acc.processedQuantity += applicableQuantity;

      return acc;
    },
    { totalCost: 0, remainingQuantity: quantity, processedQuantity: 0 },
  );

  return result.totalCost;
}

export function getPrice(
  tiers: Maybe<ItemPriceTier[]>,
  quantity: number,
): string {
  return getSubtotal(tiers, quantity).toFixed(2);
}

export function useAccountBilling() {
  const { identity } = useAuthContext();
  const { hasPermission: canReadBilling } = useIsGranted({
    permission: "account.read.billing",
  });

  const { data, isLoading: isLoadingGetBilling } =
    trpc.account.getBilling.useQuery(undefined, {
      enabled: !!identity && canReadBilling,
    });

  const subscriptionStatus = data?.subscriptionStatus ?? null;
  const trialDaysLeft = data?.trialDaysLeft;
  const currentSeats = data?.currentSeats ?? 0;
  const seatAddon = data?.addons.find(
    (addon) => addon.itemType === SubscriptionItemType.Seat,
  );
  const seats = seatAddon?.quantity ?? 0;
  const projectAddon = data?.addons.find(
    (addon) => addon.itemType === SubscriptionItemType.Project,
  );
  const seatTiers = seatAddon?.tiers ?? [];
  const discounts = data?.discounts ?? [];
  const baseUnitAmount = data?.baseUnitAmount ?? null;

  function trialStatus() {
    if (!trialDaysLeft || subscriptionStatus !== "trialing") return null;

    if (trialDaysLeft >= 1) {
      const days = Math.floor(trialDaysLeft);
      return `Your trial ends in ${pluralize(days, "day")}.`;
    } else if (trialDaysLeft >= 0) {
      return `Your trial ends today.`;
    }

    return null;
  }

  return {
    isLoadingGetBilling,
    subscriptionStatus,
    trialDaysLeft: Number.isNaN(trialDaysLeft)
      ? null
      : Math.floor(trialDaysLeft ?? 0),
    trialDaysStatus: trialStatus(),
    hasPaymentMethod: data?.hasPaymentMethod ?? false,
    currentSeats,
    seatLimit: seats,
    isSeatLimitReached: currentSeats >= seats,
    seatPrice: getPrice(seatTiers, seats),
    seatMaxQuantity: seatAddon?.maxQuantity ?? null,
    seatTiers,
    basePrice: ((baseUnitAmount ?? 0) / 100).toFixed(2),
    planName: data?.planName ?? null,
    currentProjects: data?.currentOrganizations ?? 0,
    projectLimit: projectAddon?.quantity ?? null,
    projectTiers: projectAddon?.tiers ?? [],
    projectPrice: getPrice(projectAddon?.tiers, projectAddon?.quantity ?? 0),
    cancelAt: data?.cancelAt ?? null,
    isFreePlan: data?.isFreePlan ?? false,
    planSlug: data?.planSlug ?? null,
    interval: data?.interval ?? null,
    pendingChange: data?.pendingChange ?? null,
    hasLiveSubscription: liveSubscriptionStatuses.some(
      (status) => status === subscriptionStatus,
    ),
    frequency: data?.frequency,
    discounts,
    seatsMembers: data?.seatsMembers ?? [],
  };
}

// Conditions to show limited NOAN functionalities
export function useIsPreviewMode() {
  const { isAccountActive } = useAuthContext();

  return !isAccountActive;
}
